"use client";

import Link from "next/link";
import { useState } from "react";
import { ui } from "@/components/ui";
import { authClient } from "@/lib/auth-client";

export function SignUpForm() {
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email"));
    setPending(true);
    setError(null);
    const { error } = await authClient.signUp.email({
      name: String(form.get("name")),
      email,
      password: String(form.get("password")),
      callbackURL: "/projects",
    });
    setPending(false);
    if (error) setError(error.message ?? "Could not create the account.");
    else setSentTo(email);
  }

  if (sentTo) {
    return (
      <div className={ui.card}>
        <h1 className="text-xl font-semibold">Check your email</h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
          We sent a verification link to <strong>{sentTo}</strong>. Open it to activate your account.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={`${ui.card} space-y-4`}>
      <h1 className="text-xl font-semibold">Create account</h1>
      {error && <p className={ui.error} role="alert">{error}</p>}
      <div>
        <label htmlFor="name" className={ui.label}>Name</label>
        <input id="name" name="name" required autoComplete="name" className={ui.input} />
      </div>
      <div>
        <label htmlFor="email" className={ui.label}>Email</label>
        <input id="email" name="email" type="email" required autoComplete="email" className={ui.input} />
      </div>
      <div>
        <label htmlFor="password" className={ui.label}>Password</label>
        <input
          id="password"
          name="password"
          type="password"
          required
          minLength={8}
          maxLength={128}
          autoComplete="new-password"
          className={ui.input}
        />
        <p className="mt-1 text-xs text-zinc-500">At least 8 characters.</p>
      </div>
      <button type="submit" disabled={pending} className={`${ui.button} w-full`}>
        {pending ? "Creating account…" : "Create account"}
      </button>
      <p className="text-center text-sm text-zinc-600 dark:text-zinc-400">
        Already have an account? <Link href="/sign-in" className={ui.link}>Sign in</Link>
      </p>
    </form>
  );
}
